import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, X, Check, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = `${getBackendUrl()}/api`;

/**
 * PhotoUploadPermissionsDialog
 * Admin-only. Lets the admin grant photo-upload rights on this project to
 * specific employees/sub-contractors who don't have the global photos.upload
 * permission. Admins always have access regardless.
 */
export default function PhotoUploadPermissionsDialog({ projectId, projectName, open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);  // candidate users (employee + sub_contractor)
  const [allowed, setAllowed] = useState(new Set());

  useEffect(() => {
    if (!open || !projectId) return;
    let cancelled = false;
    setLoading(true);
    const load = async () => {
      // Load each independently so one failing endpoint doesn't blank the whole dialog
      let usersOk = true;
      let allowedOk = true;
      try {
        const usersRes = await axios.get(`${API}/users`);
        if (!cancelled) {
          const all = Array.isArray(usersRes.data) ? usersRes.data : [];
          // Accept both 'sub_contractor' (underscore) and legacy 'sub-contractor' (hyphen)
          const candidates = all.filter((u) => {
            const r = (u.role || '').toLowerCase();
            return r === 'employee' || r === 'sub_contractor' || r === 'sub-contractor';
          });
          candidates.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
          setUsers(candidates);
        }
      } catch (err) {
        usersOk = false;
        console.error('[PhotoUploadPermissions] /users failed:', err?.response?.status, err?.message);
      }
      try {
        const allowedRes = await axios.get(`${API}/projects/${projectId}/photo-upload-allowed`);
        if (!cancelled) setAllowed(new Set(allowedRes.data?.user_ids || []));
      } catch (err) {
        allowedOk = false;
        console.error('[PhotoUploadPermissions] /photo-upload-allowed failed:', err?.response?.status, err?.message);
      }
      if (!cancelled) {
        setLoading(false);
        if (!usersOk && !allowedOk) {
          toast.error('Failed to load permissions. Please reload the page.');
        } else if (!usersOk) {
          toast.error("Couldn't load the team list. Please reload.");
        } else if (!allowedOk) {
          toast.error("Couldn't load current selections. Saving will reset them.");
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, projectId]);

  const toggle = (id) => {
    setAllowed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}/photo-upload-allowed`, { user_ids: [...allowed] });
      toast.success('Photo upload permissions updated');
      onOpenChange(false);
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" data-testid="photo-upload-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <Camera className="w-5 h-5 text-orange-600" />
            Photo Upload Permissions
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            Pick which employees and sub-contractors can upload photos to
            <span className="font-semibold"> {projectName || 'this project'}</span>.
            Admins always have access.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">Loading…</div>
        ) : users.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">
            <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            No employees or sub-contractors to manage.
          </div>
        ) : (
          <div className="space-y-1 max-h-[55vh] overflow-y-auto" data-testid="permission-user-list">
            {users.map((u) => {
              const checked = allowed.has(u.id);
              return (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => toggle(u.id)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${
                    checked ? 'border-orange-300 bg-orange-50' : 'border-gray-100 bg-white hover:bg-gray-50'
                  }`}
                  data-testid={`permission-toggle-${u.id}`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                    checked ? 'bg-orange-500 border-orange-500' : 'bg-white border-gray-300'
                  }`}>
                    {checked && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-semibold text-gray-900 truncate">{u.name}</p>
                    <p className="text-[11px] text-gray-500 truncate">{u.email} · {u.role === 'sub_contractor' ? 'Sub-contractor' : 'Employee'}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex gap-2 pt-3 border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1"
            data-testid="cancel-permissions-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || loading}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="save-permissions-btn"
          >
            <Check className="w-4 h-4 mr-1" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
